import { FC } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAdminOrganizationControllerFindMany } from "service";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "@/shared/components/header-items/HeaderItems";
import HeaderStandardActions from "@/shared/components/header-items/HeaderStandardActions";
import DashboardContentLayout from "@/shared/layouts/DashboardContentLayout";
import EmptyState from "@/shared/components/EmptyState";
import { AdRoutes } from "@/constants/ad-routes";
import useOrganizationsFilters from "../hooks/useOrganizationsFilters";
import OrganizationsList from "../components/OrganizationsList";

const OrganizationManagementPage: FC = () => {
  const navigate = useNavigate();
  const { filters, updateFilter, queryParams } = useOrganizationsFilters();

  const { data, isLoading, isError, refetch } =
    useAdminOrganizationControllerFindMany({ queryParams });

  const isEmpty = !isLoading && (data?.data?.length ?? 0) === 0;

  const renderContent = () => {
    if (isError) {
      return (
        <Box mt="20px">
          <EmptyState
            title="Couldn't load organizations"
            description="Something went wrong while loading organizations. Please try again."
            actionLabel="Retry"
            onAction={() => refetch()}
          />
        </Box>
      );
    }

    if (isLoading) {
      return (
        <Box mt="40px" display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    if (isEmpty) {
      return (
        <Box mt="20px">
          <EmptyState
            title="No Organizations Yet"
            description="Organizations you create will appear here."
            actionLabel="Add Organization"
            onAction={() => navigate(AdRoutes.addOrganization)}
          />
        </Box>
      );
    }

    return (
      <OrganizationsList
        data={data?.data}
        filters={filters}
        updateFilter={updateFilter}
      />
    );
  };

  return (
    <DashboardContentLayout horizontalPadding="40px">
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              { label: "Manage Organizations", url: AdRoutes.organizations },
            ]}
          />
          <HeaderItemsRight>
            <HeaderStandardActions
              onRefresh={refetch}
              onAdd={() => navigate(AdRoutes.addOrganization)}
              addButtonText="Add Organization"
            />
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      {renderContent()}
    </DashboardContentLayout>
  );
};

export default OrganizationManagementPage;
